import { useMutation, useQuery } from "@apollo/client";
import { Card, Grid } from "@mui/material";
import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { EditPersonMutation, EditPersonMutationVariables, GetRolesQuery } from "../../__generated__/graphql";
import { GET_ROLES } from "../../features/graphql/query";
import { PersonDataType, useSelectedPerson } from "../../context/StaffContext/StaffProvider";
import { Header, SmallerHeader } from "ui/components/ManagePageComponents";
import { mapApiFieldValidationErrors, FormSubmitCallback, ApiValidationError, } from "ui/components/Form"
import { useManageModal, SelectOption, } from "ui";
import { StaffTableLinkPreview, EDIT_PERSON_MUTATION, useExpirePerson, usePersonStudentAssignment } from "ft-shared-graphql";
import { StaffForm, StaffFormSchema } from "ft-shared-graphql/components/Forms"
// import usePersonStudentAssignment from "./hooks/usePersonStudentAssignment";
// import { useExpirePerson } from "./hooks/useExpirePerson";
import usePersonStore from "../store/personStore";
interface ManageDetailsProps {
    onBack: () => void,
    onLink: (id: string, action: string) => void,
}

type RouteParams = {
    id: string
}

export default function ManageStaffPage({
    onBack,
    onLink,
}: ManageDetailsProps) {

    const params = useParams<RouteParams>();

    const { person, setSelectedPerson } = useSelectedPerson(params.id);

    const myId = usePersonStore(state => state.id)
    const { expirePerson } = useExpirePerson();

    const { openModal, closeModal } = useManageModal();

    const { save } = usePersonStudentAssignment(params.id ?? '');

    const { loading, data: roleDocument } = useQuery<GetRolesQuery>(GET_ROLES);

    const [editPerson] = useMutation<EditPersonMutation, EditPersonMutationVariables>(EDIT_PERSON_MUTATION);

    const [personRoles, setPersonRoles] = useState<SelectOption[] | null | undefined>([]);

    const [removeRowId, setRemoveRowId] = useState<string | null>(null);

    useEffect(() => {
        const roles = roleDocument?.roles.map((role): SelectOption => ({
            label: role.name,
            value: role.normalizedName,
            isDefault: role.default
        }));
        setPersonRoles(roles)
    }, [loading, roleDocument]);


    const handleEditPersonSubmission: FormSubmitCallback<StaffFormSchema> = async (values: any):
        Promise<ReturnType<FormSubmitCallback>> => {

        const response = await editPerson({
            variables: {
                input: {
                    id: person?.id,
                    firstName: values.firstName,
                    middleName: values.middleName,
                    lastName: values.lastName,
                    email: values.email,
                    jobTitle: values.jobTitle,
                    userRole: values.roleType,
                    startDate: person?.startDate
                }
            }
        });

        if (response.data?.editPerson.errors) {

            const validationError = response.data.editPerson.errors
                .find(e => e.__typename === "ValidationError") as ApiValidationError;

            const fieldErrors = validationError
                ? mapApiFieldValidationErrors(validationError)
                : null;

            return fieldErrors;
        }

        const replace = {
            ...person,
            // SO dirty. Essientially it's the same data, but the type is different.
            // People should look into better, shared type management with fragment
            ...response.data?.editPerson.person
        } as PersonDataType

        setSelectedPerson(replace);
        openModal({ type: "SAVED_CHANGES", cancelButtonLabel: "Close" })

        // console.log('Submitting the details form', values);
    }

    const handleExpire = async () => {
        if (person?.id == myId) {
            openModal({ type: "CANNOT_ARCHIVE_SELF", cancelButtonLabel: "Close" })
        } else if (!person?.expired) {
            openModal({
                type: 'EXPIRE_PERSON',
                confirmButtonType: 'WARNING',
                confirmButtonLabel: 'Expire',
                onConfirm: async () => {
                    openModal(state => ({
                        ...state,
                        status: 'SAVING'
                    }))

                    await expirePerson(person?.id)

                    closeModal();
                }
            })
        } else {
            openModal({
                type: 'REACTIVATE',
            })
        }
    }

    const handleUnassignment = async (id: string) => {
        setRemoveRowId(id);
        openModal({
            type: 'REMOVE_ASSIGNMENT',
            confirmButtonType: 'WARNING',
            confirmButtonLabel: 'Yes',
            onConfirm: async () => {
                openModal(state => ({
                    ...state,
                    status: 'SAVING'
                }));

                const unassign = [id];
                await save([], unassign);

                setRemoveRowId(null);

                closeModal();
            },
            onCancel: () => {
                setRemoveRowId(null);
            },
        })
    }

    return (
        <Card
            elevation={4}
            style={{
                width: "100%",
                display: "flex",
                padding: "16px",
                height: "auto",
            }}
        >
            <Grid container spacing={2}>
                <Grid size={6} spacing={1}>
                    <Header
                        onBack={onBack}
                        title={person
                            ? `Details for ${person?.firstName} ${person?.lastName}`
                            : 'Loading...'}
                    />
                    <StaffForm
                        roles={personRoles ?? []}
                        rolesLoading={loading}
                        initialValues={{
                            firstName: person?.firstName ?? '',
                            middleName: person?.middleName,
                            lastName: person?.lastName ?? '',
                            email: person?.email ?? '',
                            jobTitle: person?.jobTitle,
                            roleType: person?.role?.normalizedName as "ADMIN" | "STANDARD" | "OFFICE_ADMIN" | "SUPER_ADMIN" | null
                        }}
                        readonly={person?.expired}
                        onSubmit={handleEditPersonSubmission}
                        onCancel={onBack}
                    />
                </Grid>
                <Grid size={6}>
                    <SmallerHeader
                        entityId={person?.id ?? ''}
                        startDate={person?.startDate}
                        endDate={person?.endDate}
                        expired={person?.expired}
                        showExpire
                        onExpire={handleExpire} />
                    {!person?.expired && (
                        <StaffTableLinkPreview
                            selectedPerson={person}
                            removeRowId={removeRowId}
                            onLink={onLink}
                            onRemove={handleUnassignment}
                            onDisabled={(reason) => {
                                openModal({ type: reason })
                            }} />
                    )}
                </Grid>
            </Grid>
        </Card>
    );
}
